<template>
  <div class="h-screen overflow-hidden flex flex-col bg-white border-solid border-l border-0 border-gray-border">
    <div class="border-0 border-b border-solid border-gray-border pb-6 px-6">
      <div class="mt-8 inline-block pr-14 relative">
        <h3 class="font-semibold text-gray-dark">
          {{ bot.name }} / <span class="text-black">{{ selectedChannel.name }}</span>
        </h3>
      </div>
    </div>

    <settings-bots-form v-if="isAmioChat"
                        :selected-theme="selectedTheme"
                        :selected-lang-config="selectedLangConfig"
                        :is-text-override-enabled="isTextOverrideEnabled"
                        :themes="themes"
                        @update:selected-theme="updateSelectedTheme"
                        @update:selected-lang-config="updateSelectedLangConfig"
                        @update:is-text-override-enabled="updateIsTextOverrideEnabled"
    />

    <div
      class="px-6 py-4 border-0 border-t border-solid border-gray-border flex items-center w-full gap-4"
      :class="isAmioChat ? 'justify-between' : 'justify-end'"
    >
      <busy-button v-if="isAmioChat"
                   :id="BUSY_BUTTONS.DEPLOY_CHAT"
                   variant="primary"
                   class="w-1/2 h-10"
                   :is-loading="isDeploying"
                   @click.prevent.stop="deployChat"
      >
        Deploy chat
      </busy-button>
      <base-button
        :externalLink="externalChatUrl"
        target="_blank"
        rel="noopener noreferrer"
        variant="bordered"
        class="w-1/2 flex items-center h-10"
        :disabled="!externalChatUrl"
      >
        Try now
        <OpenInNewIcon class="h-5 w-5"/>
      </base-button>
    </div>

    <SettingsBotSnippet :is-visible="isSnippetModalVisible"
                        :bot="bot"
                        :selected-channel="selectedChannel"
                        :selected-theme="selectedTheme"
                        :selected-lang-config="selectedLangConfig"
                        :selected-language="selectedLanguage"
                        @update:is-visible="isSnippetModalVisible = $event"
    />
  </div>
</template>

<script>
import InputWithErrorMessage from '@/components/common/InputWithErrorMessage.vue'
import SideListMenu from '@/components/common/SideListMenu'
import { CHANNEL_TYPE_ICON_MAP, CHANNEL_TYPES } from '@/utils/channel.constants'
import isEmpty from 'ramda/src/isEmpty'
import isNil from 'ramda/src/isNil'
import equals from 'ramda/src/equals'
import isEqual from 'ramda/src/equals'
import type from 'ramda/src/type'
import filter from 'ramda/src/filter'
import map from 'ramda/src/map'
import pipe from 'ramda/src/pipe'
import reject from 'ramda/src/reject'
import anyPass from 'ramda/src/anyPass'
import findIndex from 'ramda/src/findIndex'
import propEq from 'ramda/src/propEq'
import adjust from 'ramda/src/adjust'
import mergeLeft from 'ramda/src/mergeLeft'
import omit from 'ramda/src/omit'
import BusyButton from '@/components/common/BusyButton.vue'
import BaseButton from '@/components/base-components/BaseButton.vue'
import SettingsBotSnippet from '@/components/settings/bots/SettingsBotSnippet.vue'
import SettingsBotsForm from '@/components/settings/bots/SettingsBotsForm.vue'
import OpenInNewIcon from '@/assets/images/icons/OpenInNewIcon.vue'
import chatThemeService from '@/services/amio/chat-theme.service'
import { BOT_TYPES } from '@/utils/bot.constants'
import busyButtonsModule from '@/store/modules/busy-buttons.module'
import { BUSY_BUTTONS } from '@/utils/busy-buttons.constants'
import errorUtils from '@/utils/error.utils'
import toaster from '@/utils/toaster'
import { AMIO_DEMO_CHAT_URL, DEFAULT_THEME } from '@/constants/chat-theme.constants'
import botService from '@/services/amio/bot.service'
import botModule from '@/store/modules/bot.module'

export default {
  name: 'SettingsBotEditor',

  components: {
    SettingsBotsForm,
    SettingsBotSnippet,
    BaseButton,
    BusyButton,
    InputWithErrorMessage,
    SideListMenu,
    OpenInNewIcon
  },

  props: {
    bot: {
      type: Object,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isRenameMode: {
      type: Boolean,
      default: false
    },
    selectedChannel: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    return {
      botName: '',
      selectedTheme: {
        themeConfig: {},
        langConfig: {}
      },
      isSnippetModalVisible: false,
      isTextOverrideEnabled: false,
      themes: [],
      originalTheme: {},
      BUSY_BUTTONS,
      CHANNEL_TYPE_ICON_MAP
    }
  },

  watch: {
    selectedChannel: {
      handler(channel) {
        this.isTextOverrideEnabled = !!this.selectedTheme.langConfig?.[channel.id]
      }
    },
    selectedTheme: {
      handler(theme) {
        this.isTextOverrideEnabled = !!theme.langConfig?.[this.selectedChannel.id]
      }
    }
  },

  computed: {
    externalChatUrl() {
      if (!this.selectedChannel?.type) {
        return null
      }

      const channel = this.selectedChannel

      switch (channel.type) {
        case CHANNEL_TYPES.AMIO_CHAT:
          return this.buildAmioChatUrl(channel)
        case CHANNEL_TYPES.TELEGRAM:
          return `https://t.me/${channel.telegram_username}`
        case CHANNEL_TYPES.FACEBOOK_MESSENGER:
          return `https://m.me/${channel.facebook_page_id}`
        case CHANNEL_TYPES.WHATSAPP:
          return `https://wa.me/${channel.whatsapp_phone_number}`
        default:
          return null
      }
    },

    isAmioChat() {
      return this.selectedChannel.type === CHANNEL_TYPES.AMIO_CHAT && this.bot.type !== BOT_TYPES.EMAIL
    },

    isDeploying() {
      return !!busyButtonsModule.isButtonBusy(BUSY_BUTTONS.DEPLOY_CHAT)
    },

    selectedLangConfig() {
      return this.selectedTheme.langConfig?.[this.selectedChannel.id] || this.selectedTheme.langConfig.default
    },

    selectedLanguage() {
      return this.bot.language
    }
  },

  methods: {
    isEmpty,

    buildAmioChatUrl(channel) {
      let baseUrl = `${AMIO_DEMO_CHAT_URL}/?channelId=${channel.id}&startOpened=true`
      if (process.env.VUE_APP_AMIO_MESSAGING_APP_URL?.includes('staging')) {
        baseUrl += '&useStaging=true'
      }

      if (this.selectedLanguage) {
        // chat-widget-web: used as key in i18n strings for merging langConfig
        baseUrl += `&lang=${this.selectedLanguage}`
      }

      if (this.selectedTheme?.themeConfig) {
        baseUrl += `&themeOverride=${encodeURIComponent(JSON.stringify(this.selectedTheme.themeConfig))}`
      }

      if (this.selectedLangConfig) {
        baseUrl += `&langOverride=${encodeURIComponent(JSON.stringify(this.selectedLangConfig))}`
      }

      if (this.selectedLangConfig?.buttonTitle) {
        baseUrl += '&wideButton=true'
      }

      if (!this.selectedTheme?.themeConfig.header?.logoOnly) {
        baseUrl += `&logoUrl=${this.selectedTheme.themeConfig.header?.logoUrl}`
      }

      if (this.selectedTheme?.themeConfig.launcher?.imageUrl) {
        baseUrl += `&launcherImageUrl=${this.selectedTheme.themeConfig.launcher?.imageUrl}`
      }

      return baseUrl
    },

    updateBotList(chatThemeId) {
      const bots = botModule.getBotList()
      const selectedBotIndex = findIndex(propEq('id', this.bot.id), bots)
      const updatedBots = adjust(
        selectedBotIndex,
        mergeLeft({ chat_theme_id: chatThemeId }),
        bots
      )
      botModule.setBotList(updatedBots)
    },

    updateIsTextOverrideEnabled(isTextOverrideEnabled) {
      if (isTextOverrideEnabled) {
        this.selectedTheme = {
          ...this.selectedTheme,
          langConfig: {
            ...this.selectedTheme.langConfig,
            [this.selectedChannel.id]: {
              welcome: this.selectedTheme.langConfig?.default?.welcome || '',
              buttonTitle: this.selectedTheme.langConfig?.default?.buttonTitle || ''
            }
          }
        }
        this.isTextOverrideEnabled = true
      } else {
        if (this.selectedTheme.langConfig[this.selectedChannel.id]) {
          this.selectedTheme = {
            ...this.selectedTheme,
            langConfig: omit([this.selectedChannel.id], this.selectedTheme.langConfig)
          }
          this.isTextOverrideEnabled = false
        }
      }
    },

    async deployChat() {
      busyButtonsModule.setButtonBusy(BUSY_BUTTONS.DEPLOY_CHAT, true)
      try {
        let chatThemeId = this.selectedTheme?.id
        const hasThemeChanged = !isEqual(this.selectedTheme, this.originalTheme)

        if (this.selectedTheme && chatThemeId && hasThemeChanged) {
          await chatThemeService.update(this.selectedTheme)
        } else if (this.selectedTheme && !chatThemeId) {
          const theme = await chatThemeService.create(this.selectedTheme)
          this.themes.push(theme)
          chatThemeId = theme.id
          this.selectedTheme = theme
        }
        if (this.bot.chat_theme_id !== chatThemeId) {
          await botService.update(this.bot.id, {
            chat_theme_id: chatThemeId
          })
          this.updateBotList(chatThemeId)
        }
        this.isSnippetModalVisible = true
      } catch (err) {
        const errorDescription = errorUtils.resolveMessage(err)
        toaster.error(errorDescription)
      } finally {
        busyButtonsModule.setButtonBusy(BUSY_BUTTONS.DEPLOY_CHAT, false)
      }
    },

    async initBot() {
      this.botName = this.bot.name

      try {
        if (!this.bot.chat_theme_id && isEmpty(this.themes)) return
        if (!this.bot.chat_theme_id) {
          this.selectedTheme = this.themes[0]
        } else {
          this.selectedTheme = await chatThemeService.get(this.bot.chat_theme_id)
        }
        this.originalTheme = JSON.parse(JSON.stringify(this.selectedTheme))
      } catch (err) {
        toaster.error(err.message, err)
      }
    },

    cleanObject(obj) {
      if (isNil(obj) || type(obj) !== 'Object') {
        return obj
      }

      const isFalsy = anyPass([isNil, equals(''), isEmpty])

      const cleanedEntries = pipe(
        Object.entries,
        reject(([key, value]) => isFalsy(value)),
        map(([key, value]) => {
          if (type(value) === 'Object') {
            const cleanedValue = this.cleanObject(value)
            return isEmpty(cleanedValue) ? null : [key, cleanedValue]
          }
          return [key, value]
        }),
        filter(entry => entry !== null)
      )(obj)

      return Object.fromEntries(cleanedEntries)
    },

    updateSelectedLangConfig(langConfig) {
      if (this.isTextOverrideEnabled) {
        const cleanLangConfig = langConfig ? this.cleanObject(langConfig) : {}
        this.selectedTheme.langConfig[this.selectedChannel.id] = cleanLangConfig
      }
    },

    updateSelectedTheme(theme) {
      const cleanThemeConfig = theme.themeConfig ? this.cleanObject(theme.themeConfig) : {}
      const cleanLangConfig = theme.langConfig ? this.cleanObject(theme.langConfig) : {}

      this.selectedTheme = {
        ...theme,
        themeConfig: cleanThemeConfig,
        langConfig: cleanLangConfig
      }
    }
  },

  async mounted() {
    this.themes = await chatThemeService.list()
    if (!this.themes.length) {
      this.selectedTheme = DEFAULT_THEME
    }
    await this.initBot()
  }
}
</script>
